import {
  Rocket,
  CalendarDays,
  FileText,
  Settings2,
} from 'lucide-react';

/**
 * Admin guided tutorial chapters registry.
 *
 * Maintainers: when adding admin-facing UI, add/update steps here, add matching
 * `data-tour-id` anchors in the UI, and bump the chapter `version` so admins
 * who already completed the chapter see an "Updated" badge. See
 * `.cursor/rules/tutorial-knowledge.mdc`.
 */
export const TUTORIAL_CHAPTERS = [
  {
    id: 'getting-started',
    title: 'Getting Started',
    description: 'Learn the Hub layout, zones, and key shortcuts.',
    icon: Rocket,
    version: 2,
    steps: [
      {
        id: 'welcome',
        title: 'Welcome to K&R Hub',
        body: 'This guided tour walks you through the admin app in short chapters. Use Next to continue, or open the Tutorial Hub anytime from the ? icon in the command bar.',
        placement: 'center',
      },
      {
        id: 'zones',
        title: 'Three zones',
        body: 'The Hub is organized into Dispatch, Invoicing, and Management. Click a zone to switch context — each has its own pages and tools in the nav below.',
        targetSelector: '[data-tour-id="zone-switcher"]',
        placement: 'bottom',
      },
      {
        id: 'command-palette',
        title: 'Command palette (⌘K)',
        body: 'Press ⌘K (or Ctrl+K) to jump anywhere quickly — search customers, service calls, or use navigation shortcuts.',
        targetSelector: '[data-tour-id="command-palette-btn"]',
        placement: 'bottom',
      },
      {
        id: 'assistant',
        title: 'Hub Assistant (⌘J)',
        body: 'Press ⌘J to open the AI assistant. Ask "how do I…" questions about dispatch, invoicing, users, and settings.',
        targetSelector: '[data-tour-id="assistant-btn"]',
        placement: 'bottom',
      },
      {
        id: 'messages',
        title: 'Technician messages',
        body: 'Open Messages to chat with field technicians. Unread counts appear here and in the status bar.',
        targetSelector: '[data-tour-id="messages-btn"]',
        placement: 'bottom',
      },
      {
        id: 'help',
        title: 'Tutorial Hub',
        body: 'Click ? anytime to reopen this hub, restart a chapter, or pick up where you left off.',
        targetSelector: '[data-tour-id="tutorial-help-btn"]',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'dispatch',
    title: 'Dispatch',
    description: 'Schedule calls, use the backlog, docks, and auto-schedule.',
    icon: CalendarDays,
    version: 6,
    steps: [
      {
        id: 'dispatch-intro',
        title: 'Dispatch workspace',
        body: 'Dispatch is your home for scheduling and assigning field work. We\'ll tour the calendar view and key panels.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        placement: 'center',
      },
      {
        id: 'dispatch-views',
        title: 'Calendar, Board, Map, and Today',
        body: 'Switch views with these buttons or keyboard keys 1–4. Calendar for scheduling, Board for kanban reassignment, Map for geography, Today for live field progress.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="dispatch-views"]',
        placement: 'bottom',
      },
      {
        id: 'calendar-tech-filter',
        title: 'Technician filters',
        body: 'Use the technician checkboxes atop the calendar to choose whose visits you see. All techs start selected; uncheck any to hide them, or Select all to show everyone again. Swimlanes respect the same filter.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="calendar-tech-filter"]',
        placement: 'bottom',
      },
      {
        id: 'backlog-dock',
        title: 'Backlog dock',
        body: 'Unscheduled calls wait here. Drag cards onto the calendar or board to assign dates and technicians. Filter and collapse sections as needed.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="backlog-dock"]',
        placement: 'right',
      },
      {
        id: 'auto-schedule',
        title: 'Auto-Schedule',
        body: 'Propose optimized routes for a date range. Pick an optimization strategy (least travel, most visits, farthest-first, etc.) or compare 2–4 strategies side-by-side, then preview and apply.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="auto-schedule-btn"]',
        placement: 'right',
      },
      {
        id: 'incomplete-day-review',
        title: 'Incomplete Day Review',
        body: 'When a tech does not finish a day, use Incomplete Days to reschedule optimally, leave stops for the tech, or get squeeze-in slot recommendations.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="incomplete-day-review-btn"]',
        placement: 'right',
      },
      {
        id: 'detail-dock',
        title: 'Detail dock',
        body: 'Click any backlog card, calendar call, or board assignment to open full customer and call details here — contact info, generator specs, service history, and more.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="detail-dock"]',
        placement: 'left',
      },
      {
        id: 'tech-dock',
        title: 'Tech dock',
        body: 'At the bottom, see each technician\'s assignments for the selected day. Drag between columns to reassign, or message a tech from their column.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'calendar',
        targetSelector: '[data-tour-id="tech-dock"]',
        placement: 'top',
      },
      {
        id: 'board-view',
        title: 'Board view',
        body: 'The board organizes work by technician in a kanban layout — great for drag-and-drop reassignment across the team.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'board',
        targetSelector: '[data-tour-id="dispatch-views"]',
        placement: 'bottom',
      },
      {
        id: 'map-view',
        title: 'Map view',
        body: 'The map shows service locations and technician GPS (when shared). Use it for route planning and geographic clustering.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'map',
        targetSelector: '[data-tour-id="dispatch-views"]',
        placement: 'bottom',
      },
      {
        id: 'today-view',
        title: 'Today view',
        body: 'Today shows each technician\'s visits for the day in route order — done, in progress, and remaining. Use it to track field progress; click a visit for full details in the Detail Dock.',
        route: '/dispatch',
        zone: 'dispatch',
        dispatchView: 'today',
        targetSelector: '[data-tour-id="dispatch-views"]',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'invoicing',
    title: 'Invoicing',
    description: 'Review, finalize, and send completed work like an email inbox.',
    icon: FileText,
    version: 6,
    steps: [
      {
        id: 'invoicing-intro',
        title: 'Invoicing zone',
        body: 'Invoicing works like an email client: mailboxes and folders on the left, a list of calls in the middle, and the full review/edit pane on the right.',
        route: '/invoicing',
        zone: 'invoicing',
        placement: 'center',
      },
      {
        id: 'invoicing-mailboxes',
        title: 'Three mailboxes',
        body: 'Customers (contracts + repairs), Franklin, and C Spire each get their own Inbox, Completed, and Starred folders. Click a mailbox name to expand its folders.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-sidebar"]',
        placement: 'right',
      },
      {
        id: 'invoicing-zero-inbox',
        title: 'Zero-inbox by design',
        body: 'Inbox and Starred both drain automatically: once you Mark Finalized, an item disappears from Inbox and Starred and moves to Completed. Goal is Inbox at zero — Starred just flags "still needs attention," it\u2019s not a permanent bookmark. A finalized item keeps its star, so you can still find it later in Completed with the star filter, or an archive-scoped smart folder.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-folder-starred"]',
        placement: 'right',
      },
      {
        id: 'invoicing-add-folder',
        title: 'Custom folders',
        body: 'Click "+ Add folder" under any mailbox to create your own manual folder (you move items in yourself) or a smart folder that auto-filters by rules like repair/priority, missing invoice # (set Has invoice # to No), not in QuickBooks (Added to QuickBooks = No), or starred. Pick a scope: Active drains like Inbox, Archive only shows finalized items like Completed, and All ignores finalized status for standing tags.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-add-folder-customers"]',
        placement: 'right',
      },
      {
        id: 'invoicing-message-list',
        title: 'Message list',
        body: 'Rows show the customer, tech, completed date, invoice #, and checklist progress. Select multiple rows to bulk Mark QB or Finalize, use Load more when a folder has more than one page, click the star to flag an item, or use the "…" menu to move an item into a manual folder.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-message-list"]',
        placement: 'right',
      },
      {
        id: 'invoicing-status-bar',
        title: 'Billing checklist',
        body: 'The status bar tracks invoice #, QuickBooks, print, and (for Franklin/C Spire) send — some steps check automatically, others toggle manually. Mark Finalized warns if steps are still incomplete so you can finish them or continue anyway.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-status-bar"]',
        placement: 'top',
      },
      {
        id: 'invoicing-action-bar',
        title: 'Action bar',
        body: 'Customers and Franklin get Print, Download PDF, and Mark Finalized here (Franklin also has Send). For C Spire, Print/PDF/Send live in the invoice editor above — Mark Finalized is still on this bar and moves the item from Inbox to Completed.',
        route: '/invoicing',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="invoicing-action-bar"]',
        placement: 'top',
      },
      {
        id: 'service-call-lookup',
        title: 'Service Call Lookup',
        body: 'Search and open any service call by customer, date, or ID — useful for questions after invoicing or historical review.',
        route: '/service-calls',
        zone: 'invoicing',
        targetSelector: '[data-tour-id="nav-/service-calls"]',
        placement: 'bottom',
      },
    ],
  },
  {
    id: 'management',
    title: 'Management',
    description: 'Customers, users, scheduling, reports, inventory, and settings.',
    icon: Settings2,
    version: 5,
    steps: [
      {
        id: 'management-intro',
        title: 'Management zone',
        body: 'Management holds master data and configuration — customers, users, reports, and system settings.',
        route: '/customers',
        zone: 'management',
        placement: 'center',
      },
      {
        id: 'customers',
        title: 'Customers & Sites',
        body: 'Manage customer records, generator details, flags, and site information. Approve tech-submitted pending customers here.',
        route: '/customers',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/customers"]',
        placement: 'bottom',
      },
      {
        id: 'users',
        title: 'Users',
        body: 'Add and edit admin and technician accounts. Set technician capabilities, auto-schedule inclusion, and daily capacity.',
        route: '/users',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/users"]',
        placement: 'bottom',
      },
      {
        id: 'tech-scheduling',
        title: 'Tech Scheduling',
        body: 'Configure technician schedules, blackouts, home address (optional route start), and availability patterns used by dispatch and auto-schedule.',
        route: '/tech-scheduling',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/tech-scheduling"]',
        placement: 'bottom',
      },
      {
        id: 'reports',
        title: 'Reports',
        body: 'Monthly roster and technician metrics — track workload, completion rates, and roster planning.',
        route: '/reports',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/reports"]',
        placement: 'bottom',
      },
      {
        id: 'inventory',
        title: 'Inventory',
        body: 'Parts catalog with per-location stock (Main Warehouse + technician trucks). Open an item to Receive, Adjust, or Transfer stock and view the transaction history. Parts used on jobs auto-deduct from the assigned tech\'s truck.',
        route: '/inventory',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/inventory"]',
        placement: 'bottom',
      },
      {
        id: 'inventory-locations',
        title: 'Inventory Locations',
        body: 'View and manage warehouse and truck stock locations. Each technician has an auto-provisioned truck location. Transfer parts from the warehouse to restock trucks.',
        route: '/inventory-locations',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/inventory-locations"]',
        placement: 'bottom',
      },
      {
        id: 'settings',
        title: 'Settings',
        body: 'Advanced configuration: scheduling defaults, shop address, travel time, invoice templates, sales tax by state, client flags, and more.',
        route: '/settings',
        zone: 'management',
        targetSelector: '[data-tour-id="nav-/settings"]',
        placement: 'bottom',
      },
      {
        id: 'settings-state-tax',
        title: 'Sales tax by state',
        body: 'Open Settings → Invoicing. Use Sales tax by state to set the rate and which line categories (Labor, Parts, Other) are taxable per state. Mark individual customers tax exempt when editing a customer.',
        route: '/settings',
        zone: 'management',
        targetSelector: '[data-tour-id="invoice-settings-state-tax"]',
        placement: 'top',
      },
    ],
  },
];

export const getChapterById = (chapterId) =>
  TUTORIAL_CHAPTERS.find((chapter) => chapter.id === chapterId) ?? null;

export const GETTING_STARTED_CHAPTER_ID = 'getting-started';
